/**
 * Césures conditionnelles à la française (refonte F1, lot 2).
 *
 * Les étiquettes des nœuds de l'arbre tiennent dans un couloir d'environ
 * 120 px en 390 px : un mot long (« Dodécaédrique », « Modification ») doit
 * pouvoir se couper. `hyphens: auto` dépend des dictionnaires du navigateur
 * (absents sur une partie des Chromium de bureau) : on pose donc nous-mêmes des
 * **traits d'union conditionnels** (U+00AD), invisibles tant que le mot tient.
 *
 * Règles syllabiques simplifiées, suffisantes pour des noms de talents :
 *  - une consonne entre deux voyelles part avec la seconde (Do·dé·ca) ;
 *  - deux consonnes se séparent (Bol·le), sauf les groupes inséparables
 *    (bl, cr, ch, gn, qu…) qui partent ensemble (ma·chine) ;
 *  - trois consonnes et plus : coupe avant le groupe final inséparable, sinon
 *    avant la dernière consonne ;
 *  - jamais moins de trois lettres de part et d'autre de la coupe, jamais dans
 *    un mot de moins de `minWord` lettres.
 *
 * Module **pur** (aucun DOM) : testé dans `tests/ui/hyphen.test.ts`.
 */

export const SOFT_HYPHEN = '­';

const VOWELS = new Set('aeiouyàâäéèêëîïôöùûüÿœæ');
/** Groupes de deux consonnes qui ne se séparent pas. */
const CLUSTERS = new Set([
  'bl', 'cl', 'fl', 'gl', 'pl', 'br', 'cr', 'dr', 'fr', 'gr', 'pr', 'tr', 'vr', 'ch', 'ph', 'th', 'gn', 'qu', 'gu',
]);
const MIN_EDGE = 3;

function isVowel(ch: string): boolean {
  return VOWELS.has(ch.toLowerCase());
}

function isLetter(ch: string): boolean {
  return ch.toLowerCase() !== ch.toUpperCase();
}

/** Positions de coupe (index du premier caractère de la syllabe suivante). */
export function breakPoints(word: string): number[] {
  const out: number[] = [];
  const chars = [...word];
  const n = chars.length;
  if (!chars.every(isLetter)) return out;
  let i = 0;
  // Avance jusqu'à la première voyelle.
  while (i < n && !isVowel(chars[i])) i++;
  while (i < n) {
    // Fin du noyau vocalique courant.
    while (i < n && isVowel(chars[i])) i++;
    // Consonnes qui suivent.
    const start = i;
    while (i < n && !isVowel(chars[i])) i++;
    if (i >= n) break; // consonnes finales : pas de coupe
    const count = i - start;
    let cut: number;
    if (count <= 1) {
      cut = start;
    } else {
      const lastTwo = (chars[i - 2] + chars[i - 1]).toLowerCase();
      cut = CLUSTERS.has(lastTwo) ? i - 2 : i - 1;
    }
    // « qu », « gu » : le u est une consonne de fait ; rien de plus à gérer ici.
    if (cut >= MIN_EDGE && n - cut >= MIN_EDGE) out.push(cut);
  }
  return out;
}

/**
 * Pose des césures conditionnelles dans les mots d'au moins `minWord` lettres.
 * Les traits d'union et les espaces existants sont gardés tels quels (le
 * navigateur y coupe déjà).
 */
export function softHyphenate(text: string, minWord = 9): string {
  return text.replace(/[\p{L}]+/gu, (word) => {
    if ([...word].length < minWord) return word;
    const chars = [...word];
    const points = new Set(breakPoints(word));
    let out = '';
    chars.forEach((ch, idx) => {
      if (points.has(idx)) out += SOFT_HYPHEN;
      out += ch;
    });
    return out;
  });
}
